import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmModal } from '../../components/Modal/DeleteConfirmModal';
import { useApiKeys } from '../../hooks/useApiKeys';
import { ApiKey } from '../../types/apiKey';
import { EditApiKeyModal } from './EditApiKeyModal';

export const ApiKeyList = () => {
  const { t } = useTranslation();
  const { apiKeys, isLoading, deleteApiKey } = useApiKeys();
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-xl">
      <table className="table w-full">
        <thead>
          <tr>
            <th>{t('apiKeys.table.name')}</th>
            <th>{t('apiKeys.table.key')}</th>
            <th>{t('apiKeys.table.roles')}</th>
            <th>{t('apiKeys.table.status')}</th>
            <th>{t('apiKeys.table.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {apiKeys.map((apiKey) => (
            <tr key={apiKey.key}>
              <td>
                <div>
                  <div className="font-bold">{apiKey.name}</div>
                  {apiKey.description && (
                    <div className="text-sm opacity-70">{apiKey.description}</div>
                  )}
                </div>
              </td>
              <td>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                  {apiKey.key}
                </code>
              </td>
              <td>
                <div className="flex flex-wrap gap-1">
                  {apiKey.roles.map((role) => (
                    <span key={role} className="badge badge-primary badge-sm">
                      {role}
                    </span>
                  ))}
                </div>
              </td>
              <td>
                <span
                  className={`badge ${
                    apiKey.status === 'ACTIVE' ? 'badge-success' : 'badge-error'
                  }`}
                >
                  {apiKey.status}
                </span>
              </td>
              <td>
                <div className="flex gap-2">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelectedApiKey(apiKey)}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm text-error"
                    onClick={() => setKeyToDelete(apiKey.key)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedApiKey && (
        <EditApiKeyModal
          isOpen={!!selectedApiKey}
          onClose={() => setSelectedApiKey(null)}
          apiKey={selectedApiKey}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!keyToDelete}
        onClose={() => setKeyToDelete(null)}
        onConfirm={() => {
          if (keyToDelete) {
            deleteApiKey.mutate(keyToDelete);
            setKeyToDelete(null);
          }
        }}
      />
    </div>
  );
}; 